/**
 * THE shared length editor — used by the 2D dim-chip editor (DimsOverlay) and
 * every measurement field in the panels. Its layout follows the units
 * preference: feet and inches as two fields with ' and " marks (inches to a
 * tenth; values over 11 carry into feet on commit — 7' 12" → 8'), inches as
 * one field with a " mark, feet as one decimal field with a ' mark. Whatever
 * the units, the model rounds every committed length to the half inch.
 *
 * Value in/out is DECIMAL FEET. Commit fires on Enter or on focus leaving
 * the group (tabbing between two fields does not commit); Escape resets to
 * the canonical value and calls onCancel. After every commit the fields
 * re-key so they snap back to the (possibly clamped) model value.
 */

import { css } from '@emotion/react';
import { type MeasurementUnits, splitFeetInches, useFloorPlanPreferences } from '@kaiizen/kai-floor-plan-editor-core';
import { useEffect, useRef, useState } from 'react';
import { bone, fieldWidth, focusRing, ink, midtone, white } from '../styles';

const LENGTH_GROUP_STYLE = css`
  label: LENGTH_GROUP_STYLE;
  display: inline-flex; align-items: baseline; gap: 3px;
  box-sizing: border-box; width: ${fieldWidth};
  padding: 6px 8px; border: 1px solid ${bone};
  background: ${white}; color: ${ink}; font: inherit;
  &:focus-within {
    border-color: ${ink};
    box-shadow: ${focusRing};
  }
  span { color: ${midtone}; }
`;

const NUMBER_BOX_STYLE = css`
  label: NUMBER_BOX_STYLE;
  min-width: 0; text-align: right;
  border: none; outline: none; background: none; padding: 0;
  font: inherit; color: inherit;
  &::-webkit-outer-spin-button, &::-webkit-inner-spin-button {
    -webkit-appearance: none; margin: 0;
  }
  -moz-appearance: textfield;
`;

export interface LengthInputProps {
  /** Decimal feet from the model; null renders blank (host decides what blank means). */
  value: number | null;
  /** Always fires on commit; null when every field is blank. */
  onCommit: (valueFeet: number | null) => void;
  /** Escape — the fields reset either way; the chip editor uses this to close. */
  onCancel?: () => void;
  autoFocus?: boolean;
  /** data-set hook on the group (the applyPanelInput key; also the E2E selector). */
  setKey?: string;
  /** Permit a leading minus on the first field (e.g. free-element X/Y overhanging the room). */
  allowNegative?: boolean;
}

/** The fields' text for a length's magnitude in the given units. */
function fieldTexts(units: MeasurementUnits, magnitudeFeet: number): { first: string; inches: string } {
  if (units === 'feetInches') {
    const split = splitFeetInches(magnitudeFeet, 0.1);
    return { first: String(split.feet), inches: String(split.inches) };
  }
  if (units === 'inches') return { first: String(Math.round(magnitudeFeet * 120) / 10), inches: '' };
  return { first: String(Math.round(magnitudeFeet * 100) / 100), inches: '' };
}

/** Decimal feet (magnitude) from the fields' raw text; the sign is the caller's. */
function lengthFromFields(units: MeasurementUnits, firstRaw: string, inchesRaw: string): number {
  const first = Math.abs(parseFloat(firstRaw) || 0);
  if (units === 'feetInches') return Math.round(first) + Math.max(0, Math.round((parseFloat(inchesRaw) || 0) * 10) / 10) / 12;
  if (units === 'inches') return Math.round(first * 10) / 10 / 12;
  return Math.round(first * 100) / 100;
}

export function LengthInput({
  value,
  onCommit,
  onCancel,
  autoFocus,
  setKey,
  allowNegative,
}: LengthInputProps): React.JSX.Element {
  const { units } = useFloorPlanPreferences();
  const groupRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLInputElement>(null);
  const inchesRef = useRef<HTMLInputElement>(null);
  // Escape sets this so the resulting focus loss doesn't also commit.
  const suppressCommitRef = useRef(false);
  // Bumped on every commit/cancel: re-keys the fields to the canonical value.
  const [epoch, setEpoch] = useState(0);

  const negative = value != null && value < 0;
  const texts = fieldTexts(units, Math.abs(value ?? 0));
  const firstText = value == null ? '' : `${negative ? '-' : ''}${texts.first}`;
  const inchesText = value == null ? '' : texts.inches;
  const twoFields = units === 'feetInches';

  useEffect(() => {
    if (!autoFocus) return;
    firstRef.current?.focus();
    firstRef.current?.select();
  }, [autoFocus]);

  const commit = () => {
    const firstRaw = firstRef.current?.value.trim() ?? '';
    const inchesRaw = twoFields ? inchesRef.current?.value.trim() ?? '' : '';
    setEpoch(n => n + 1);
    if (firstRaw === '' && inchesRaw === '') {
      onCommit(null);
      return;
    }
    const sign = allowNegative && firstRaw.startsWith('-') ? -1 : 1;
    onCommit(sign * lengthFromFields(units, firstRaw, inchesRaw));
  };

  return (
    <div
      css={LENGTH_GROUP_STYLE}
      ref={groupRef}
      data-set={setKey}
      onBlur={e => {
        if (groupRef.current?.contains(e.relatedTarget as Node)) return;
        if (suppressCommitRef.current) {
          suppressCommitRef.current = false;
          return;
        }
        commit();
      }}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLElement).blur();
        if (e.key === 'Escape') {
          suppressCommitRef.current = true;
          setEpoch(n => n + 1);
          onCancel?.();
        }
      }}
    >
      <input
        css={NUMBER_BOX_STYLE}
        key={`${units}-f${firstText}-${epoch}`}
        ref={firstRef}
        type='number'
        step={units === 'feet' ? 0.01 : units === 'inches' ? 0.1 : 1}
        min={allowNegative ? undefined : 0}
        defaultValue={firstText}
        placeholder='–'
        aria-label={units === 'inches' ? 'inches' : 'feet'}
        style={{ width: twoFields ? 28 : 60 }}
      />
      <span>{units === 'inches' ? '"' : "'"}</span>
      {twoFields && (
        <>
          <input
            css={NUMBER_BOX_STYLE}
            key={`i${inchesText}-${epoch}`}
            ref={inchesRef}
            type='number'
            step={0.1}
            min={0}
            defaultValue={inchesText}
            placeholder='–'
            aria-label='inches'
            style={{ width: 36 }}
          />
          <span>&quot;</span>
        </>
      )}
    </div>
  );
}
